import React from "react";
import { Field, BaseProps } from "./field";
import { EditorState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import { makeStyles } from "@material-ui/core/styles";
import "../../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

type Props = BaseProps<EditorState> & {
  maxLength?: number;
  placeholder?: string;
  rows?: number;
  rowsMax?: number;
};

const useClassnames = makeStyles((theme) => ({
  wrapper: {
    borderStyle: "solid",
    borderWidth: "1px",
    borderColor: (props: Props) =>
      props.error ? theme.palette.error.main : theme.palette.grey[300],
  },
  toolbar: {
    backgroundColor: theme.palette.grey[200],
    borderRadius: 0,
    margin: 0,
    border: "none",
    padding: "5px 3px 0",
    fontFamily: "sans-serif",
  },
  editor: {
    border: "none",
    minHeight: "95px",
    cursor: "text",
    "& .public-DraftStyleDefault-block": {
      margin: 0,
      padding: "6px",
    },
  },
  colorPickerPopUp: {
    top: "32px",
    left: "5px",
    padding: 0,
    boxShadow: "1px 1px 0 #BFBDBD",
    height: "auto",
    width: "146px",
    border: "none",
    borderRadius: 0,
    "& > .rdw-colorpicker-modal-header": {
      padding: "1px 1px 0",
      "& > .rdw-colorpicker-modal-style-label": {
        padding: "5px",
        fontSize: "x-small",
        textAlign: "left",
        backgroundColor: theme.palette.grey[300],
        "&.rdw-colorpicker-modal-style-label-active": {
          backgroundColor: "#FFF",
          border: "none",
        },
      },
    },
    "& > .rdw-colorpicker-modal-options": {
      margin: 0,
      padding: "1px",
      "& > .rdw-colorpicker-option": {
        margin: 0,
        padding: "1px",
        background: "none",
        boxShadow: "none",
        "& > .rdw-colorpicker-cube": {
          border: "none",
          boxShadow: "inset 0px 0px 1px #000",
        },
      },
    },
  },
  fontFamily: {
    width: "138px",
    "& > .rdw-fontfamily-optionwrapper": {
      boxSizing: "border-box",
      fontSize: "x-small",
      margin: "0 -1px",
      "& > .rdw-dropdownoption-default": {
        minHeight: "22px",
      },
    },
  },
  fontSize: {
    width: "68px",
    "& > .rdw-dropdown-optionwrapper": {
      width: "70px",
      boxSizing: "border-box",
      fontSize: "x-small",
      margin: "0 -1px",
      "& > .rdw-dropdownoption-default": {
        minHeight: "22px",
      },
      "& > .rdw-fontsize-option": {
        justifyContent: "left",
      },
    },
  },
  linkPopUp: {
    top: "32px",
    left: "5px",
    padding: "1px",
    boxShadow: "1px 1px 0 #BFBDBD",
    width: "160px",
    height: "auto",
    border: "none",
    borderRadius: 0,
    "& > .rdw-link-modal-target-option": {
      padding: "5px",
    },
    "& > .rdw-link-modal-buttonsection": {
      textAlign: "center",
    },
    "& > .rdw-link-modal-label": {
      padding: "4px",
      fontSize: "medium",
    },
    "& > .rdw-link-modal-input": {
      width: "100%",
      height: "30px",
      margin: 0,
      borderColor: theme.palette.grey[300],
      borderRadius: 0,
      boxSizing: "border-box",
    },
  },
  imagePopUp: {
    top: "32px",
    left: "5px",
    padding: "1px",
    boxShadow: "1px 1px 0 #BFBDBD",
    width: "160px",
    border: "none",
    borderRadius: 0,
    "& > .rdw-image-modal-header, & > .rdw-image-modal-size, & > .rdw-image-modal-btn-section": {
      display: "none",
      visibility: "hidden",
    },
    "& > .rdw-image-modal-url-section": {
      flexDirection: "column",
      "&::before": {
        content: "'Image url'",
        display: "block",
        boxSizing: "border-box",
        padding: "4px",
        fontSize: "x-small",
        width: "100%",
      },
      "& > .rdw-image-modal-url-input": {
        width: "100%",
        height: "22px",
        margin: 0,
        borderColor: theme.palette.grey[300],
        borderRadius: 0,
        boxSizing: "border-box",
      },
      "& > .rdw-image-mandatory-sign": {
        display: "none",
      },
    },
  },
}));

export const RichTextEditorForAnnouncements: React.FC<Props> = (props) => {
  const classnames = useClassnames(props);

  return (
    <Field
      label={props.label}
      required={props.required}
      error={props.error}
      disabled={props.disabled}
      size={props.size}
    >
      <Editor
        editorState={props.value}
        onEditorStateChange={(state) => {
          if (props.onChange) {
            props.onChange(props.name, state);
          }
        }}
        placeholder={props.placeholder}
        readOnly={props.disabled}
        toolbar={{
          options: ["inline", "fontFamily", "list", "link", "remove"],
          inline: {
            options: ["bold", "italic", "underline"],
          },
          fontFamily: {
            options: [
              "Normal",
              "Times New Roman",
              "Arial",
              "Courier New",
              "Georgia",
              "Trebuchet",
              "Verdana",
            ],
            className: classnames.fontFamily,
          },
          list: {
            options: ["ordered", "unordered"],
          },
          link: {
            popupClassName: classnames.linkPopUp,
          },
        }}
        wrapperClassName={classnames.wrapper}
        toolbarClassName={classnames.toolbar}
        editorClassName={classnames.editor}
      />
    </Field>
  );
};

export { EditorState };
